import React from 'react';
import { 
  Leaf, 
  Phone, 
  MessageCircle, 
  Mail, 
  MapPin, 
  ShieldCheck, 
  Heart,
  Lock
} from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import { useStore } from '../../context/StoreContext';

const Footer = ({ onSelectCategory, onOpenAdminLogin }) => {
  const { lang, t } = useLanguage();
  const { settings, cmsContent, categories } = useStore();

  const footerAbout = lang === 'ur'
    ? (cmsContent?.footer?.aboutUrdu || 'فاضل طب والجراحت (FTJ) • ماہر قانون مفرد اعضاء (طبِ پاکستانی)۔ تمام ادویات و مرکبات صابر ملتانیؒ کے مصدقہ سائنسی و قرابادینی اصولوں کے تحت تیار کی جاتی ہیں۔')
    : (cmsContent?.footer?.aboutEn || 'Certified Practitioner (FTJ) in Tibb-e-Pakistani (Qanoon-e-Mufrad Aza). Formulations crafted under rigorous botanical and tissue-balance principles.');

  const clinicTimings = lang === 'ur'
    ? (cmsContent?.footer?.clinicTimingsUrdu || 'روزانہ صبح 10:00 بجے تا شام 8:00 بجے (جمعہ نماز کا وقفہ)')
    : (cmsContent?.footer?.clinicTimingsEn || 'Daily 10:00 AM to 8:00 PM (Friday prayer break)');

  const clinicAddress = lang === 'ur'
    ? (cmsContent?.footer?.clinicAddressUrdu || settings.addressUrdu)
    : (cmsContent?.footer?.clinicAddressEn || settings.addressEn);

  return (
    <footer className={`bg-pansar-950 text-white border-t border-pansar-800 ${lang === 'ur' ? 'font-urdu' : 'font-sans'}`}>
      
      {/* Top Banner inside Footer */}
      <div className="border-b border-pansar-800/80 bg-pansar-900/40 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 flex flex-col md:flex-row items-center justify-between gap-6 text-center md:text-start">
          <div className="flex items-center gap-3">
            {settings?.logoUrl ? (
              <div className="w-12 h-12 rounded-2xl overflow-hidden border border-amber-500/50 bg-white flex items-center justify-center shadow-lg p-1">
                <img src={settings.logoUrl} alt={settings.storeNameUrdu || "Store Logo"} className="w-full h-full object-contain" />
              </div>
            ) : (
              <div className="w-12 h-12 rounded-2xl bg-amber-500 text-slate-950 flex items-center justify-center font-bold shadow-lg">
                <Leaf className="w-7 h-7" />
              </div>
            )}
            <div className="text-start">
              <h3 className="font-bold text-xl text-white">
                {lang === 'ur' ? settings.storeNameUrdu : settings.storeNameEn}
              </h3>
              <p className="text-xs text-emerald-300">
                {lang === 'ur' ? settings.dawakhanaNameUrdu : settings.dawakhanaNameEn}
              </p>
            </div>
          </div>

        </div>
      </div>

      {/* Main Footer Links */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 text-start">
          
          {/* Col 1: Brand & Hakim Bio */}
          <div className="space-y-3">
            <h4 className="text-base font-bold text-amber-400 border-b border-pansar-800 pb-2">
              {lang === 'ur' ? 'طبی سرپرستی' : 'Medical Patronage'}
            </h4>
            <p className="text-sm font-bold text-white">
              {lang === 'ur' ? settings.hakimNameUrdu : settings.hakimNameEn}
            </p>
            <p className="text-xs text-gray-300 leading-relaxed">
              {footerAbout}
            </p>
            <div className="pt-2 text-xs text-emerald-400 flex items-center gap-2">
              <ShieldCheck className="w-4 h-4 text-amber-400 shrink-0" />
              <span>{lang === 'ur' ? 'کیش آن ڈلیوری کی مکمل سہولت' : 'Nationwide Cash on Delivery'}</span>
            </div>
          </div>

          {/* Col 2: Categories Links */}
          <div className="space-y-3">
            <h4 className="text-base font-bold text-amber-400 border-b border-pansar-800 pb-2">
              {lang === 'ur' ? 'اقسام ادویات' : 'Categories'}
            </h4>
            <ul className="space-y-2 text-xs text-gray-300">
              {(categories || []).map(cat => (
                <li key={cat.id}>
                  <button
                    onClick={() => onSelectCategory(cat.id)}
                    className="hover:text-amber-300 transition text-start cursor-pointer"
                  >
                    • {lang === 'ur' ? cat.nameUrdu : cat.nameEn}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Col 3: Payment & Delivery Information */}
          <div className="space-y-3">
            <h4 className="text-base font-bold text-amber-400 border-b border-pansar-800 pb-2">
              {lang === 'ur' ? 'طریقہ ادائیگی و ترسیل' : 'Payment & Delivery'}
            </h4>
            <p className="text-xs text-gray-300 leading-relaxed">
              {lang === 'ur'
                ? (cmsContent?.footer?.deliveryColTextUrdu || 'ہم پورے پاکستان کے تمام شہروں، قصبوں اور دیہاتوں میں بذریعہ کوریئر بحفاظت پارسل روانہ کرتے ہیں۔')
                : (cmsContent?.footer?.deliveryColTextEn || 'We deliver safely via courier across all cities, towns, and villages of Pakistan.')}
            </p>
            <div className="space-y-1.5 text-xs text-gray-300">
              {(settings.enableCod ?? true) && (
                <div>📦 <strong>{lang === 'ur' ? 'کیش آن ڈلیوری (COD)' : 'Cash on Delivery (COD)'}</strong></div>
              )}
              <div>
                💳 <strong>{lang === 'ur' ? 'ایزی پیسہ (EasyPaisa):' : 'EasyPaisa:'}</strong> <span className="font-mono dir-ltr">{settings.phone}</span>
              </div>
              <div>
                🏦 <strong>{lang === 'ur' ? 'یو بی ایل (UBL Bank):' : 'UBL Bank:'}</strong> <span className="font-mono text-[11px] dir-ltr inline-block">{settings.bankIban || 'PK56UNIL0109000290123386'}</span>
              </div>
            </div>
          </div>

          {/* Col 4: Contact & Admin */}
          <div className="space-y-3">
            <h4 className="text-base font-bold text-amber-400 border-b border-pansar-800 pb-2">
              {lang === 'ur' ? 'رابطہ اور مقام' : 'Contact & Location'}
            </h4>
            <ul className="space-y-2.5 text-xs text-gray-300">
              <li className="flex items-start gap-2">
                <MapPin className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />
                <a
                  href={cmsContent?.socialLinks?.googleMaps || "https://maps.app.goo.gl/pHyFP7WjwTa3hqis6"}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-amber-300 transition underline underline-offset-2"
                  title="Google Maps"
                >
                  {clinicAddress}
                </a>
              </li>
              <li className="flex items-center gap-2 text-emerald-200">
                <span className="font-bold">{lang === 'ur' ? 'اوقاتِ کار:' : 'Timings:'}</span>
                <span>{clinicTimings}</span>
              </li>
              <li className="flex items-center gap-2">
                <Phone className="w-4 h-4 text-emerald-400 shrink-0" />
                <span className="font-sans dir-ltr">{settings.phone}</span>
              </li>
              <li className="flex items-center gap-2">
                <Mail className="w-4 h-4 text-emerald-400 shrink-0" />
                <span className="font-sans">{settings.email}</span>
              </li>
            </ul>

            {/* Social Media Links */}
            {(cmsContent?.socialLinks?.facebook || cmsContent?.socialLinks?.youtube || cmsContent?.socialLinks?.instagram || cmsContent?.socialLinks?.googleMaps) && (
              <div className="pt-2 border-t border-pansar-800/80">
                <span className="text-[11px] text-gray-400 block mb-1.5 font-urdu">
                  {lang === 'ur' ? 'سوشل میڈیا روابط:' : 'Social Links:'}
                </span>
                <div className="flex items-center gap-2">
                  {cmsContent?.socialLinks?.facebook && (
                    <a
                      href={cmsContent.socialLinks.facebook}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-8 h-8 rounded-lg bg-pansar-900 hover:bg-blue-600 text-gray-300 hover:text-white flex items-center justify-center transition border border-pansar-700"
                      title="Facebook"
                    >
                      <span className="font-bold text-xs font-sans">FB</span>
                    </a>
                  )}
                  {cmsContent?.socialLinks?.youtube && (
                    <a
                      href={cmsContent.socialLinks.youtube}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-8 h-8 rounded-lg bg-pansar-900 hover:bg-rose-600 text-gray-300 hover:text-white flex items-center justify-center transition border border-pansar-700"
                      title="YouTube"
                    >
                      <span className="font-bold text-xs font-sans">YT</span>
                    </a>
                  )}
                  {cmsContent?.socialLinks?.instagram && (
                    <a
                      href={cmsContent.socialLinks.instagram}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-8 h-8 rounded-lg bg-pansar-900 hover:bg-pink-600 text-gray-300 hover:text-white flex items-center justify-center transition border border-pansar-700"
                      title="Instagram"
                    >
                      <span className="font-bold text-xs font-sans">IG</span>
                    </a>
                  )}
                  {cmsContent?.socialLinks?.googleMaps && (
                    <a
                      href={cmsContent.socialLinks.googleMaps}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-8 h-8 rounded-lg bg-pansar-900 hover:bg-emerald-600 text-gray-300 hover:text-white flex items-center justify-center transition border border-pansar-700"
                      title="Google Maps Location"
                    >
                      <MapPin className="w-3.5 h-3.5" />
                    </a>
                  )}
                </div>
              </div>
            )}
          </div>

        </div>

        {/* Disclaimer */}
        <div className="mt-10 pt-6 border-t border-pansar-900 text-center text-xs text-gray-400 space-y-2">
          <p className="max-w-3xl mx-auto leading-relaxed">
            {lang === 'ur' 
              ? (cmsContent?.footer?.disclaimerUrdu || 'طبی انتباہ و ڈسکلیمر: ہماری تمام ادویات اور نباتاتی مرکبات طبِ پاکستانی (قانون مفرد اعضاء) کے اصولوں پر تیار کیے جاتے ہیں۔ کسی بھی شدید ایمرجنسی، حاملہ خواتین اور مستقل بیماری کی صورت میں اپنے قریبی مستند معالج سے رجوع فرمائیں۔')
              : (cmsContent?.footer?.disclaimerEn || 'Medical Disclaimer: All our herbal formulations are crafted strictly under the principles of Tibb-e-Pakistani (Qanoon-e-Mufrad Aza). In case of medical emergencies, pregnancy, or persistent chronic illness, please consult a certified medical practitioner.')}
          </p>
          <div className="text-gray-400 text-[11px] pt-2">
            © {new Date().getFullYear()} {lang === 'ur' 
              ? (cmsContent?.footer?.copyrightUrdu || `${settings.storeNameUrdu} | ${settings.dawakhanaNameUrdu} | جملہ حقوق محفوظ ہیں۔`)
              : `${settings.storeNameEn || 'Himalayan Pansar Store'} • All Rights Reserved.`}
          </div>
        </div>

      </div>
    </footer>
  );
};

export default Footer;
